import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { PlusIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ErrorType } from "@/types";
import { MemberTableDatatypes } from "@/types/member";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { DataTableViewOptions } from "./data-table-view-options";
import {
  useGetAllBusinessesQuery,
  useGetAllMemberQuery,
  useUpdateMemberMutation,
  useLazyGetAllMemberForExportQuery,
} from "@/services/memberApi";
import { useGetMembershipListQuery, useGetMembershipsQuery } from "@/services/membershipsApi";
import MemberForm from "../modal/form";
import TableFilters from "@/components/ui/table/data-table-filter";
import {
  displayDate,
  displayDateTime,
  displayValue,
  downloadCSV,
  membersMapper,
} from "@/lib/helper";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { UserStatus } from "@/types/enums";
import { useGetCoachesQuery } from "@/services/coachApi";

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  status?: string;
  membership_plan?: string;
  coach_asigned?: string;
  type: string;
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "member",
};

const status = [
  { value: "active", label: "Active", color: "bg-green-500" },
  { value: "inactive", label: "Inactive", color: "bg-blue-500" },
  { value: "pending", label: "Pending", color: "bg-orange-500", hide: true },
];
interface FilterDataType {
  [key: string]: string | number | boolean; // Define the types you expect for your filters
}

export default function MemberTableView() {
  const navigate = useNavigate();
  const member = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).member ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const [action, setAction] = useState<"add" | "edit" | "member-created">(
    "add"
  );
  const [open, setOpen] = useState<boolean>(false);
  const [editMember, setEditMember] = useState<MemberTableDatatypes | null>(
    null
  );
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const {
    searchCriteria,
    query,
    toggleSortOrder,
    setSearchCriteria,
    inputValue,
    setInputValue,
  } = useTableData(initialValue);

  // search input
  const [openFilter, setOpenFilter] = useState(false);
  // const [filterData, setFilter] = useState({});
  const [filterData, setFilter] = useState<FilterDataType>({}); // Use the defined type for active filters
  const { data: business, refetch: refetchBusiness } =
    useGetAllBusinessesQuery(orgId);

  const {
    data: memberData,
    isLoading,
    refetch,
    error,
    isError,
  } = useGetAllMemberQuery(
    { org_id: orgId, query: query },
    {
      skip: query == "",
    }
  );

  const { data: membershipPlans, isLoading: membershipLoading } =
    useGetMembershipsQuery({
      query: `sort_key=id&sort_order=desc&status=active`,
      org_id: orgId
    }, {
      skip: !orgId,
    });

  const { data: coachesData, isLoading: coachesLoading } =
    useGetCoachesQuery({
      query: `sort_key=id&sort_order=desc`,
      org_id: orgId
    }, {
      skip: !orgId,
    });

  const membershipPlanOptions = useMemo(() => {
    return membershipPlans?.data.map((plan) => ({
      label: plan.name,
      value: plan.id
    }));
  }, [membershipPlans]);

  const coachOptions = useMemo(() => {
    return coachesData?.data.map((coach) => ({
      label: `${coach.own_coach_id} • ${coach.first_name} ${coach.last_name}`,
      value: coach.id
    }));
  }, [coachesData]);

  useEffect(() => {
    if (isError && navigator.onLine) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: typedError.data?.detail || typedError.data?.message,
      });
    }
  }, [isError]);

  function handleOpenForm() {
    setAction("add");
    setEditMember(null);
    setOpen(true);
  }

  function handleEditForm(data: MemberTableDatatypes) {
    setAction("edit");
    setEditMember(data);
    setOpen(true);
  }

  const handleSelectAllRows = () => {
    setSelectAllRows(true);
    setShowSelectAllNotification(false);
  };

  const handleClearSelection = () => {
    setSelectAllRows(false);
    setShowSelectAllNotification(false);
    table.toggleAllPageRowsSelected(false);
    setRowSelection({});
  };



  const memberTableData = React.useMemo(() => {
    return Array.isArray(memberData?.data) ? memberData?.data : [];
  }, [memberData]);
  const { toast } = useToast();

  const [sorting, setSorting] = useState<SortingState>([]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [data, setData] = useState<MemberTableDatatypes | undefined>(undefined);
  const [selectAllRows, setSelectAllRows] = useState(false);
  const [showSelectAllNotification, setShowSelectAllNotification] = useState(false);
  const [getAllMemberForExport] = useLazyGetAllMemberForExportQuery();

  // const displayValue = (value: string | undefined | null) =>
  //   value == null || value == undefined || value.trim() == "" ? "" : value;

  const handleExportSelected = async () => {
    if (selectAllRows) {
      // Export all rows
      try {
        const filterQuery = Object.keys(filterData).length > 0
          ? Object.entries(filterData)
            .filter(([_, value]) => value !== "")
            .map(([key, value]) => `${key}=${value}`)
            .join("&")
          : "";

        const result = await getAllMemberForExport({
          org_id: orgId,
          filterQuery: filterQuery
        }).unwrap();


        if (result?.data && result.data.length > 0) {
          downloadCSV(result.data, "all_members_list.csv", membersMapper);
          toast({
            variant: "success",
            title: `Exported ${result.data.length} members successfully`,
          });
        } else {
          toast({
            variant: "destructive",
            title: "No data to export",
            description: "No member records found to export.",
          });
        }
      } catch (error) {
        console.error("Export failed:", error);
        toast({
          variant: "destructive",
          title: "Export failed",
          description: "Failed to export all members. Please try again.",
        });
      }
    } else {
      // Export selected rows
      const selectedRows = table
        .getSelectedRowModel()
        .rows.map((row) => row.original);
      if (selectedRows.length === 0) {
        toast({
          variant: "destructive",
          title: "Please select one or more records to perform this action.",
        });
        return;
      }
      const updatedSelectedRows = selectedRows.map((row) => ({
        ...row,
      }));

      downloadCSV(updatedSelectedRows, "members_list.csv", membersMapper);
    }
  };

  const [updateMember] = useUpdateMemberMutation();

  const handleStatusChange = async (payload: MemberTableDatatypes) => {
    try {
      const status =
        payload.client_status == UserStatus.Active
          ? UserStatus.Inactive
          : UserStatus.Active;
      const resp = await updateMember({
        ...payload,
        client_status: status,
      }).unwrap();
      if (resp) {
        refetch();
        toast({
          variant: "success",
          title: "Member Updated Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const actionsColumn: ColumnDef<MemberTableDatatypes> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <div data-action-cell onClick={(e) => e.stopPropagation()}>
        <DataTableRowActions
          access={member}
          row={row.original.id}
          data={row?.original}
          refetch={refetch}
          handleEditMember={handleEditForm}
        />
      </div>
    ),
  };

  const columns: ColumnDef<MemberTableDatatypes>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <div className="flex items-center gap-2">
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value: any) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
            className="translate-y-[2px]"
          />
        </div>
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "own_member_id",
      meta: "Member Id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Member Id"
          sortKey="id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <div className="">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="capitalize cursor-pointer">
                      {/* Display the truncated name */}
                      {displayValue(
                        `${row.original.own_member_id}`.length > 12
                          ? `${row.original.own_member_id}`.substring(0, 12) +
                          "..."
                          : `${row.original.own_member_id}`
                      )}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    {/* Display the full name in the tooltip */}
                    <p className="capitalize text-sm">
                      {displayValue(`${row.original.own_member_id}`)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => `${row.first_name} ${row.last_name}`,
      id: "full_name",
      meta: "Member Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Member Name"
          sortKey="first_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <div className="">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="capitalize cursor-pointer">
                      {/* Display the truncated name */}
                      {displayValue(
                        `${row.original.first_name} ${row.original.last_name}`
                          .length > 12
                          ? `${row.original.first_name} ${row.original.last_name}`.substring(
                            0,
                            12
                          ) + "..."
                          : `${row.original.first_name} ${row.original.last_name}`
                      )}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    {/* Display the full name in the tooltip */}
                    <p className="capitalize text-sm">
                      {displayValue(
                        `${row.original.first_name} ${row.original.last_name}`
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => `${row.email}`,
      id: "email",
      meta: "email",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Email"
          sortKey="id"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <div className="">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild >
                    <p className="normal-case cursor-pointer">
                      {/* Display the truncated name */}
                      {displayValue(
                        `${row.original.email}`.length > 15
                          ? `${row.original.email}`.substring(0, 15) + "..."
                          : `${row.original.email}`
                      )}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    {/* Display the full name in the tooltip */}
                    <p className="normal-case ">
                      {displayValue(`${row.original.email}`)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => `${row.mobile_number}`,
      id: "mobile_number",
      meta: "mobile_number",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Phone Number"
          sortKey="id"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <div className="">
              <p className="capitalize cursor-pointer">
                {/* Display the truncated name */}
                {displayValue(`${row.original.mobile_number}`, true)}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "business_name",
      meta: "Business Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Business Name"
          sortKey="business_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="capitalize cursor-pointer">
                    {/* Display the truncated name */}
                    {!row.original.is_business &&
                      row.original.business_id == undefined
                      ? ""
                      : displayValue(
                        `${row.original.business_name}`.length > 15
                          ? `${row.original.business_name}`.substring(0, 15) +
                          "..."
                          : `${row.original.business_name}`
                      )}
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="capitalize text-sm">
                    {!row.original.is_business &&
                      row.original.business_id == undefined
                      ? ""
                      : displayValue(`${row.original.business_name}`)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
    },
    {
      accessorKey: "tax_group",
      meta: "Tax Group",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Tax Group"
          sortKey="tax_group"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="capitalize cursor-pointer">
                    {/* Display the truncated name */}
                    {row.original.tax_group && displayValue(
                      `${row.original.tax_group.name}`.length > 15
                        ? `${row.original.tax_group.name}`.substring(0, 15) +
                        "..."
                        : `${row.original.tax_group.name}`
                    )}
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="capitalize text-sm">
                    {row.original.tax_group && displayValue(`${row.original.tax_group.name}`)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
    },
    {
      accessorKey: "client_status",
      meta: "Status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="client_status"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const value = row.original?.client_status;
        const statusLabel = status.filter((r) => r.value === value)[0];
        return (
          <div data-action-cell onClick={(e) => e.stopPropagation()}>
          <Select
            value={value}
            onValueChange={(e) => handleStatusChange(row.original)}
            disabled={statusLabel.hide || member == "read"}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Status" className="text-gray-400  ">
                <span className="flex gap-2 items-center ">
                  <span
                    className={`${statusLabel?.color} rounded-[50%] w-4 h-4`}
                  ></span>
                  <span>{statusLabel?.label}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {status.map(
                (item) =>
                  !item.hide && (
                    <SelectItem key={item.value + ""} value={item.value + ""}>
                      {item.label}
                    </SelectItem>
                  )
              )}
            </SelectContent>
          </Select>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "activated_on",
      meta: "Activation Date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Activation Date"
          sortKey="activated_on"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayDate(row?.original.activated_on)}
          </div>
        );
      },
    },
    {
      accessorKey: "check_in",
      meta: "Last Check In",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Last Check In"
          sortKey="check_in"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden text-black">
            {displayDateTime(row?.original?.check_in)}
          </div>
        );
      },
    },
    {
      accessorKey: "last_online",
      meta: "Last Login",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Last Login"
          sortKey="last_online"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden text-black">
            {displayDateTime(row?.original?.last_online)}
          </div>
        );
      },
    },
    ...(member !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: memberTableData as MemberTableDatatypes[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  // Handle showing select all notification
  useEffect(() => {
    const allPageRowsSelected = table.getIsAllPageRowsSelected();
    const selectedRowCount = table.getSelectedRowModel().rows.length;
    const totalRowsInCurrentPage = table.getRowModel().rows.length;

    if (allPageRowsSelected && selectedRowCount === totalRowsInCurrentPage && totalRowsInCurrentPage > 0 && !selectAllRows) {
      setShowSelectAllNotification(true);
    } else if (selectedRowCount === 0 || selectAllRows) {
      setShowSelectAllNotification(false);
    }
  }, [table.getIsAllPageRowsSelected(), table.getSelectedRowModel().rows.length, selectAllRows]);

  // Reset select all when row selection changes
  useEffect(() => {
    if (Object.keys(rowSelection).length === 0) {
      setSelectAllRows(false);
      setShowSelectAllNotification(false);
    }
  }, [rowSelection]);

  // Auto-select all visible rows when selectAllRows is true and data changes
  useEffect(() => {
    if (selectAllRows && memberTableData.length > 0) {
      table.toggleAllPageRowsSelected(true);
    }
  }, [selectAllRows, memberTableData, table]);

  function handleFilterChange(field: string, value: string | number) {
    setFilter((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  const filterDisplay = [
    {
      type: "combobox",
      name: "membership_plan",
      label: "Membership",
      options: membershipPlanOptions,
      function: (value: string) => handleFilterChange("membership_plan", value),
    },
    {
      type: "multiselect",
      name: "coach_assigned",
      label: "Coach",
      options: coachOptions,
      function: (value: string) => handleFilterChange("coach_assigned", value),
    },

    {
      type: "select",
      name: "status",
      label: "Status",
      options: [
        { id: "pending", name: "Pending" },
        { id: "inactive", name: "Inactive" },
        { id: "active", name: "Active" },
      ],
      function: (value: string) => handleFilterChange("status", value),
    },
    {
      type: "combobox",
      name: "business_id",
      label: "Business Name",
      options: business?.map((item) => ({
        value: item.id,
        label: item.full_name,
      })),
      function: (value: string) => handleFilterChange("business_id", value),
    },
  ];

  const totalRecords = memberData?.filtered_counts || 0;

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-row items-center justify-between gap-2 px-3">
        {/* Table Filter component */}
        <div className="flex-initial sm:flex-1 min-w-0">
          <TableSearchFilter
            setSearchCriteria={setSearchCriteria}
            inputValue={inputValue}
            setInputValue={setInputValue}
          />
        </div>
        {/* Buttons Container */}
        <div className="flex flex-row items-center gap-1 sm:gap-2 shrink-0">
          <Button
            access={member !== "read"}
            className="bg-primary text-sm text-black flex items-center justify-center h-8 px-2 min-w-[32px]"
            onClick={handleOpenForm}
          >
            <PlusIcon className="size-4" />
            <span className="hidden sm:inline ml-1">Create New</span>
          </Button>

          <DataTableViewOptions table={table} action={handleExportSelected} />

          <button
            className={`border rounded-full size-8 text-gray-400 flex items-center justify-center shrink-0 ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>

      {/* Gmail-style Select All Notification */}
      {(showSelectAllNotification || selectAllRows) && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mx-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-sm text-blue-800">
              {selectAllRows ? (
                <>
                  All <strong>{totalRecords}</strong> members are selected.
                </>
              ) : (
                <>
                  All <strong>{table.getSelectedRowModel().rows.length}</strong> members on this page are selected.
                </>
              )}
            </div>
            {!selectAllRows && (
              <button
                onClick={handleSelectAllRows}
                className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
              >
                Select all {totalRecords} members
              </button>
            )}
            {selectAllRows && (
              <button
                onClick={handleClearSelection}
                className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
              >
                Clear selection
              </button>
            )}
          </div>
        </div>
      )}

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-4 px-3">
        {isLoading ? (
          <div className="flex space-x-2 justify-center items-center h-24">
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce"></div>
          </div>
        ) : memberTableData.length > 0 ? (
          memberTableData.map((row: any) => (
            <div
              key={row.id}
              className="bg-white border rounded-lg p-4 shadow-sm relative space-y-2"
              onClick={() => member !== "read" && handleEditForm(row)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-bold text-lg capitalize">{row.first_name} {row.last_name}</h3>
                  <p className="text-sm text-gray-500 uppercase">{row.own_member_id}</p>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <div className={`w-3 h-3 rounded-full ${status.find(s => s.value === row.client_status)?.color || "bg-gray-400"}`}></div>
                  <DataTableRowActions
                    access={member}
                    row={row.id}
                    data={row}
                    refetch={refetch}
                    handleEditMember={handleEditForm}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm pt-2">
                <div className="flex items-center gap-2">
                  <i className="fa-regular fa-envelope text-gray-400 w-4"></i>
                  <span className="truncate">{row.email || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-gray-400 w-4"></i>
                  <span>{row.mobile_number || "N/A"}</span>
                </div>
                {row.business_name && (
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-briefcase text-gray-400 w-4"></i>
                    <span className="truncate">{row.business_name}</span>
                  </div>
                )}
                {row.tax_group?.name && (
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-percent text-gray-400 w-4"></i>
                    <span className="truncate">Tax: {row.tax_group.name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <i className="fa-regular fa-calendar-check text-gray-400 w-4"></i>
                  <span>Active: {displayDate(row.activated_on)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-door-open text-gray-400 w-4"></i>
                  <span className="truncate">Check-in: {displayDateTime(row.check_in)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-right-to-bracket text-gray-400 w-4"></i>
                  <span className="truncate">Login: {displayDateTime(row.last_online)}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="h-24 text-center flex items-center justify-center border rounded-lg">
            No members found.
          </div>
        )}
      </div>

      <div className="hidden md:block rounded-none border border-border ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          ></ScrollBar>
          {/* <Table className="w-full overflow-x-scroll "> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (
                        e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') &&
                        !e.target.closest('[data-radix-select-trigger]') &&
                        !e.target.closest('[role="combobox"]') &&
                        !e.target.closest('[data-action-cell]')
                      ) {
                        navigate(`/admin/members/detail/${row.original.id}`);
                      }
                    }}
                    className="cursor-pointer hover:bg-secondary"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : memberTableData.length > 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No members added yet.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No members found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {memberTableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilter}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />
      <MemberForm
        open={open}
        setOpen={setOpen}
        memberData={editMember}
        setMemberData={setEditMember}
        action={action}
        setAction={setAction}
        refetch={refetch}
        breadcrumb="Dashboard"
      />
    </div>
  );
}